/**
 * Taxonomy resolver for the Global Help concierge (KAN-1358).
 *
 * Scores an inbound user message against the curated taxonomy vocabulary
 * (`taxonomy.ts`) and returns ranked `taxonomy_matches[]`. The result is
 * attached to the `ui_context` sent to CodeNexus so the router and the
 * `global-product-studio-help` skill can disambiguate intent and speak the
 * app's language.
 *
 * Design goals: cheap (called on every send), synchronous, deterministic, and
 * dependency-free. Preferred grounding — never a hard blocker; an empty result
 * simply means the router falls back to intent + workspace signals.
 */
import { TAXONOMY_TERMS, TAXONOMY_VERSION, type TaxonomyTerm } from './taxonomy';

/** A single ranked concept the message mapped to. */
export interface TaxonomyMatch {
  /** Taxonomy term id (kebab-case slug). */
  id: string;
  /** Canonical UI label for the concept. */
  ui: string;
  /** Match strength in [0, 1]; higher is stronger. */
  score: number;
  /** The phrase (ui label or alias) that produced the match. */
  matchedAlias: string;
}

/** Resolver output: ranked matches plus the taxonomy revision they came from. */
export interface TaxonomyResolution {
  matches: TaxonomyMatch[];
  version: string;
}

export interface ResolveOptions {
  /**
   * Max matches to return. Defaults to 3 (Codex secondary-hints cap). Kept as
   * an option so the platform default can change in one place.
   */
  limit?: number;
}

const DEFAULT_LIMIT = 3;

/** Lowercase, strip punctuation to spaces, collapse whitespace. */
function normalize(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Whole-phrase, word-boundary containment test on already-normalized strings.
 * `haystack` and `needle` must both be normalized. Guards against substring
 * false positives (e.g. "chat" inside "chatter") by requiring space or string
 * boundaries around the needle.
 */
function containsPhrase(haystack: string, needle: string): boolean {
  if (!needle) return false;
  const padded = ` ${haystack} `;
  return padded.includes(` ${needle} `);
}

/**
 * Score one phrase against the message. Longer, more specific phrases score
 * higher than short generic ones, so "context graph" beats a bare "graph".
 * Returns 0 when the phrase is not present as a whole phrase.
 */
function scorePhrase(normalizedMessage: string, phrase: string): number {
  const normalizedPhrase = normalize(phrase);
  if (!normalizedPhrase || !containsPhrase(normalizedMessage, normalizedPhrase)) {
    return 0;
  }
  const wordCount = normalizedPhrase.split(' ').length;
  // 1 word -> 0.6, 2 -> 0.8, 3 -> ~0.87, asymptotically approaching 1.
  return Math.min(1, 0.6 + 0.2 * (wordCount - 1));
}

/** Best (phrase, score) for a term across its ui label and aliases. */
function scoreTerm(
  normalizedMessage: string,
  term: TaxonomyTerm,
): { score: number; matchedAlias: string } {
  let best = 0;
  let bestPhrase = '';
  for (const phrase of [term.ui, ...term.aliases]) {
    const s = scorePhrase(normalizedMessage, phrase);
    if (s > best || (s === best && s > 0 && phrase.length > bestPhrase.length)) {
      best = s;
      bestPhrase = phrase;
    }
  }
  // Nudge: unconfirmed terms are slightly less trustworthy grounding.
  if (best > 0 && term.unconfirmed) best -= 0.05;
  return { score: best, matchedAlias: bestPhrase };
}

/**
 * Resolve a message to ranked taxonomy matches. Empty/whitespace input, or a
 * message that matches nothing, yields an empty `matches` array.
 */
export function resolveTaxonomy(
  message: string,
  options: ResolveOptions = {},
): TaxonomyResolution {
  const limit = options.limit ?? DEFAULT_LIMIT;
  const normalizedMessage = normalize(message ?? '');
  if (!normalizedMessage || limit <= 0) {
    return { matches: [], version: TAXONOMY_VERSION };
  }

  const scored: TaxonomyMatch[] = [];
  for (const term of TAXONOMY_TERMS) {
    const { score, matchedAlias } = scoreTerm(normalizedMessage, term);
    if (score > 0) {
      scored.push({ id: term.id, ui: term.ui, score: Number(score.toFixed(3)), matchedAlias });
    }
  }

  scored.sort(
    (a, b) =>
      b.score - a.score ||
      b.matchedAlias.length - a.matchedAlias.length ||
      a.id.localeCompare(b.id),
  );

  return { matches: scored.slice(0, limit), version: TAXONOMY_VERSION };
}
